import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { checkWorkspaceNameExists } from '../../../entities/workspace/api';
import { useWorkspaceCreationStorageState } from '../hooks/use-workspace-creation-storage-state';

import { Button } from '@/shared/components/shadcn/button';

// Validation schema
const workspaceNameSchema = z.object({
  workspaceName: z
    .string()
    .min(1, 'Workspace name is required')
    .max(20, 'Workspace names must be 20 characters or fewer')
    .regex(
      /^[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ\s\-_]+$/,
      'Only letters, numbers, spaces, hyphens and underscores allowed',
    ),
});

type WorkspaceNameFormData = z.infer<typeof workspaceNameSchema>;

export function WorkspaceNameStep() {
  const redirectPage = useSearch({
    from: '/workspace/create/name',
    select: (search) => search.redirectPage,
  });
  const navigate = useNavigate();
  const [workspaceData, setWorkspaceData] = useWorkspaceCreationStorageState();
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
  } = useForm<WorkspaceNameFormData>({
    resolver: zodResolver(workspaceNameSchema),
    mode: 'onChange',
    defaultValues: {
      workspaceName: workspaceData.workspaceName || '',
    },
  });

  const handleFormSubmit = async (data: WorkspaceNameFormData) => {
    setIsCheckingAvailability(true);

    try {
      const { exists } = await checkWorkspaceNameExists({ name: data.workspaceName });

      if (exists) {
        setError('workspaceName', {
          type: 'manual',
          message: 'The name is already taken',
        });
        return;
      }

      setWorkspaceData((prev) => ({ ...prev, workspaceName: data.workspaceName }));

      navigate({ to: '/workspace/create/language', search: { redirectPage } });
    } catch (error) {
      captureException(error);
      setError('workspaceName', {
        type: 'manual',
        message: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  return (
    <div className="bg-sidebar-background flex min-h-screen justify-center">
      <div className="mt-[240px] flex w-full max-w-[610px] flex-col gap-6 px-4">
        <header className="flex flex-row items-center gap-2 text-3xl font-bold">
          <span>👋</span>
          <h1>Welcome to Syncly social</h1>
        </header>

        <section className="flex flex-col gap-2">
          <h2 className="text-foreground text-2xl leading-none font-medium tracking-tight">
            Name your workspace
          </h2>
          <p className="text-muted-foreground text-2xl leading-none font-medium tracking-tight">
            Of course, you can edit it later.
          </p>
        </section>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="w-full">
          <div className="border-border bg-background flex items-center gap-4 rounded-md border px-3 py-2">
            <input
              {...register('workspaceName')}
              placeholder="Workspace name"
              className="text-foreground placeholder:text-muted-foreground flex-1 border-none bg-transparent text-base font-normal outline-none"
              disabled={isCheckingAvailability}
            />
            <Button
              type="submit"
              size="icon"
              className="size-10"
              disabled={!isValid || isCheckingAvailability}
            >
              <ArrowRight className="size-4" />
            </Button>
          </div>
          {errors.workspaceName && (
            <p className="text-destructive mt-2 text-sm">{errors.workspaceName.message}</p>
          )}
        </form>
      </div>
    </div>
  );
}
